import {
  PaymentMethodType,
  PaymentOption,
  PaymentSummaryResponse,
  SafeCharge,
} from '../../../helpers/utils/types';
import {
  SAFECHARGE_FIELD_BASE_STYLE,
  SAFECHARGE_FIELDS_OPTIONS,
} from '../../../constants/fontConstants';
import { useModal } from '../../../helpers/hooks/modalContext';
import { useAuth } from '../../../auth/Auth';
import { useEffect, useRef, useState } from 'react';
import {
  getPaymentSummary,
  IPIFY_API_URL,
  saveUserIpAddress,
  subscribeRent,
  verifyPaymentOption,
  directPaymentToken,
  saveUserIp,
} from '../../../auth/_requests';
import { usePayment } from '../../../helpers/hooks/PaymentContext';

import { Spinner } from '../../../helpers/UI/Spinner/Spinner';

import { Plus, AlertCircle, CreditCard, ArrowRight, RefreshCw } from 'lucide-react';
import CardCarousal from '../CardCarousal/CardCarousal';
import { motion } from 'framer-motion';
import { showToast } from '../../../helpers/utils/showToast';
import { formatApiErrorMessage } from '../../../helpers/utils/error';
import React from 'react';
import { usePaymentOptions } from '../../../helpers/hooks/PaymentOptionsContext';
import { usePointTasksContext } from '../../../helpers/hooks/pointTasksContext';
import { getTaskMaxPoints } from '../../../helpers/utils/rewardLabels';
import { logThirdPartyError } from '../../../helpers/utils/safeSentryError';
import { trackAutopayEnabled } from '../../../helpers/utils/analytics';
import {
  getNuveiRedirectUrls,
  normalizeNuveiTransactionStatus,
  saveNuveiPaymentContext,
  clearNuveiPaymentContext,
  getNuveiRawErrorDescription,
  getNuveiUserFacingErrorMessage,
  createNuvei3dsEventHandlers,
} from '../../../helpers/utils/nuveiPaymentUtils';
import {
  TenantPaymentOptionStatusEnum,
  getPaymentOptionStatusLabel,
  getPaymentOptionStatusMessage,
} from '../../../helpers/utils/types';
import axios from 'axios';
interface SafeChargeProps {
  safeCharge: SafeCharge;
  tenantBuildingId: string | undefined;
  paymentType: string;
  setIsPaying: React.Dispatch<React.SetStateAction<boolean>>;
}
const errorMessages: Record<string, string> = {
  incomplete_cvc: 'CVC is incomplete',
  invalid_cvc: 'The CVC is invalid',
};
const CardSection = React.forwardRef<any, SafeChargeProps>(
  ({ safeCharge, tenantBuildingId, paymentType, setIsPaying: setIsPayingState }, ref) => {
    // Ref updates synchronously, so a fast double click cannot start a second payment before state flushes
    const isPayingRef = useRef(false);
    const setIsPaying = (value: boolean): void => {
      isPayingRef.current = value;
      setIsPayingState(value);
    };
    const { setModal, modalState } = useModal();
    const { setPaymentSummary } = usePayment();
    const { paymentOptions, loading, refreshPaymentOptions } = usePaymentOptions();
    const { getTasksByCode } = usePointTasksContext();
    const { auth, currentUser } = useAuth();

    const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | null>(null);
    const [cvv, setCvv] = useState<string>('');

    const cardCvcRef = useRef<any>(null);
    const [, setSelectedMethod] = useState<string | null>(
      localStorage.getItem('selectedPaymentOption')
    );

    const [isCvvEntered, setIsCvvEntered] = useState<boolean>(false);
    const [verificationAmount, setVerificationAmount] = useState<string>('');
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [verificationAmountError, setVerificationAmountError] = useState<string>('');
    const [showConfirmAutopay, setShowConfirmAutopay] = useState<boolean>(false);

    const isPreAuthorizedDebit =
      selectedPaymentOption?.paymentMethod?.toLowerCase() === 'preauthorizeddebit';
    const isVerificationPending =
      selectedPaymentOption?.status === TenantPaymentOptionStatusEnum.VerificationPending;
    const rentPaymentTask = getTasksByCode(['rentPayment'])[0];
    const subscriptionTask = getTasksByCode(['subscriptionPayment'])[0];
    const earnedPoints =
      getTaskMaxPoints(rentPaymentTask) +
      (paymentType === 'subscription' ? getTaskMaxPoints(subscriptionTask) : 0);
    // const [paymentSummary, setPaymentSummary] = useState<any>(null);

    const [cardErrors, setCardErrors] = useState<any>({});

    useEffect(() => {
      if (auth?.data.token) {
        saveUserIp(auth.data.token).catch(() => {
          // Silently catch errors - IP capture is non-critical and should not block the payment flow
        });
      }
    }, [auth?.data.token]);

    useEffect(() => {
      const paymentOptionId = localStorage.getItem('selectedPaymentOption');
      if (paymentOptionId) {
        const foundOption = paymentOptions.find((option) => option.id === paymentOptionId);
        if (foundOption) {
          setSelectedPaymentOption(foundOption);
        }
      }
    }, [paymentOptions]);

    useEffect(() => {
      const handleFieldChange = (event: any) => {
        const { field, error } = event;

        if (error) {
          const friendlyMessage = errorMessages[error.id] || 'An error occurred';
          setCardErrors((prevErrors: any) => ({
            ...prevErrors,
            [field]: friendlyMessage,
          }));
        } else {
          setCardErrors((prevErrors: any) => ({
            ...prevErrors,
            [field]: '',
          }));
        }
      };

      // Only initialize CVV field for non-PreAuthorizedDebit payment methods
      if (safeCharge && selectedPaymentOption && !isPreAuthorizedDebit) {
        const safeChargeFields = safeCharge.fields({
          ...SAFECHARGE_FIELDS_OPTIONS,
        });

        // Poll for the existence of the card-cvc element
        const pollForCvcField = setInterval(() => {
          const cvcElement = document.getElementById('card-cvc');
          if (cvcElement && !cardCvcRef.current) {
            cardCvcRef.current = safeChargeFields.create('ccCvc', {
              style: {
                base: SAFECHARGE_FIELD_BASE_STYLE,
              },
            });

            cardCvcRef.current.attach('#card-cvc');
            cardCvcRef.current.on('change', (event: any) => {
              if (event.complete) {
                setIsCvvEntered(true);
              } else {
                setIsCvvEntered(false);
              }
            });
            cardCvcRef.current.on('change', handleFieldChange);

            setCvv(cardCvcRef.current);
            clearInterval(pollForCvcField); // Stop polling once the element is found
          }
        }, 100); // Poll every 100ms

        return () => clearInterval(pollForCvcField); // Cleanup the polling on unmount
      } else if (isPreAuthorizedDebit) {
        // For PreAuthorizedDebit, CVV is not required
        setIsCvvEntered(true);
        if (cardCvcRef.current) {
          cardCvcRef.current.clear();
          cardCvcRef.current = null;
        }
      }
    }, [safeCharge, selectedPaymentOption, isPreAuthorizedDebit]);

    useEffect(() => {
      const fetchPaymentOptions = async () => {
        if (!auth?.data.token) return;
        try {
          const options = paymentOptions;
          if (options.length > 0) {
            if (!selectedPaymentOption) {
              handleSelectMethod(options[0].id);
              setSelectedPaymentOption(options[0]);
            }
          }
          const paymentOptionId = localStorage.getItem('selectedPaymentOption');
          if (paymentOptionId) {
            const foundOption = options.find((option) => option.id === paymentOptionId);
            if (foundOption) {
              setSelectedPaymentOption(foundOption);
              setSelectedMethod(paymentOptionId);

              const summary = await getPaymentSummary(
                auth.data.token,
                tenantBuildingId,
                foundOption.id,
                foundOption.paymentMethod as PaymentMethodType
              );
              setPaymentSummary(summary as PaymentSummaryResponse);
            }
          }
        } catch (error) {
          showToast({
            message: 'Error fetching payment options: ' + error,
            type: 'error',
          });
        }
      };
      fetchPaymentOptions();
    }, [auth, tenantBuildingId, paymentOptions]);
    useEffect(() => {
      const refetchPaymentSummary = async () => {
        if (!auth?.data.token || !selectedPaymentOption) return;

        try {
          const summary = await getPaymentSummary(
            auth.data.token,
            tenantBuildingId,
            selectedPaymentOption.id,
            selectedPaymentOption.paymentMethod as PaymentMethodType
          );
          setPaymentSummary(summary as PaymentSummaryResponse);
        } catch (error) {
          showToast({
            message: 'Error refetching payment summary: ' + error,
            type: 'error',
          });
        } finally {
          // Reset the modal flag
          setModal(null, { refetchPaymentSummary: false });
        }
      };

      if (modalState?.refetchPaymentSummary) {
        refetchPaymentSummary();
      }
    }, [modalState, auth, tenantBuildingId, selectedPaymentOption, setPaymentSummary, setModal]);

    const handleSelectMethod = async (id: string) => {
      if (cardCvcRef.current) {
        cardCvcRef.current.clear();
      }

      const selectedOption = paymentOptions?.find((option) => option.id === id);

      if (selectedOption) {
        setSelectedMethod(id);
        localStorage.setItem('selectedPaymentOption', selectedOption.id);
        setSelectedPaymentOption({ ...selectedOption });

        try {
          const summary = await getPaymentSummary(
            auth?.data.token,
            tenantBuildingId,
            selectedOption.id,
            selectedOption.paymentMethod as PaymentMethodType
          );
          setPaymentSummary(summary as PaymentSummaryResponse);
        } catch (error) {
          showToast({
            message: 'Error fetching payment options: ' + error,
            type: 'error',
          });
        }
      }
    };
    /*  const handleCancel = () => {
    setModal('return-to-dashboard');
  }; */
    const handleActiveIndexChange = (swiper: any) => {
      const activeIndex = swiper.activeIndex;
      const activePaymentOption = paymentOptions?.[activeIndex];

      if (activePaymentOption) {
        handleSelectMethod(activePaymentOption.id);
      }
    };

    const createDirectPayment = async () => {
      // CVV is not required for PreAuthorizedDebit
      if (!isPreAuthorizedDebit && !isCvvEntered) {
        setCardErrors((prevErrors: any) => ({
          ...prevErrors,
          ccCvc: 'Please enter a valid CVV',
        }));
        return;
      }
      if (isPayingRef.current) return;
      if (!tenantBuildingId || !selectedPaymentOption?.id) {
        showToast({ message: 'Please select a payment method to continue.', type: 'error' });
        return;
      }
      if (!isPreAuthorizedDebit && !safeCharge) {
        showToast({
          message: 'Payment system is still loading. Please wait a moment and try again.',
          type: 'error',
        });
        return;
      }
      let sessionTokenForPaymentFinal = '';
      let message = '';
      try {
        setIsPaying(true);

        // Ensure IP address is sent before making payment
        if (auth?.data.token) {
          await saveUserIp(auth.data.token).catch(() => {});
        }

        const response = await directPaymentToken(
          auth?.data.token,
          tenantBuildingId,
          selectedPaymentOption.id,
          getNuveiRedirectUrls()
        );

        sessionTokenForPaymentFinal = response.sessionToken;

        message = 'success';
      } catch (error) {
        setIsPaying(false);
        const raw =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : typeof error === 'object' && error !== null && 'message' in error
                ? (error as { message: string }).message
                : 'Unknown error';

        logThirdPartyError(new Error('Direct Payment - failed to fetch payment session token'), {
          flow: 'direct_payment',
          step: 'direct_payment_token',
          tenantBuildingId,
          paymentOptionId: selectedPaymentOption?.id,
          paymentMethod: selectedPaymentOption?.paymentMethod,
          paymentProcessor: selectedPaymentOption?.paymentProcessor,
          error,
        });

        showToast({
          message: formatApiErrorMessage(raw, 'Error Initiating Payment. Please try again.'),
          type: 'error',
        });
      }

      if (message === 'success') {
        // For PAD, just openOrderWithoutAuth is sufficient
        if (isPreAuthorizedDebit) {
          setIsPaying(false);
          setModal(null);
          setModal('payment-status-direct-successfull', {
            tenantBuildingId: tenantBuildingId,
            processDate: new Date().toISOString().split('T')[0],
            tenantPaymentOptionId: selectedPaymentOption?.id,
            earnedPoints,
          });
          return;
        }

        const request = {
          sessionToken: sessionTokenForPaymentFinal,
          userTokenId: currentUser?.user?.userId,
          paymentOption: {
            userPaymentOptionId: selectedPaymentOption?.paymentOptionId,
          },
        };

        saveNuveiPaymentContext({
          flow: 'direct-pay',
          tenantBuildingId,
          tenantPaymentOptionId: selectedPaymentOption?.id,
          payPlan: 'direct',
          earnedPoints,
        });

        const nuvei3dsEvents = createNuvei3dsEventHandlers();

        try {
          safeCharge?.createPayment(
            {
              sessionToken: sessionTokenForPaymentFinal,
              userTokenId: currentUser?.user?.userId,
              paymentOption: {
                userPaymentOptionId: selectedPaymentOption?.paymentOptionId,
                ...(isPreAuthorizedDebit
                  ? {}
                  : {
                      card: {
                        CVV: cvv,
                      },
                    }),
              },
              onPaymentEvent: nuvei3dsEvents.onPaymentEvent,
            },
            async (resp) => {
              nuvei3dsEvents.clearNuvei3dsChallengeState();
              setIsPaying(false);
              const response = resp;
              const outcome = normalizeNuveiTransactionStatus(resp);
              const rawError = getNuveiRawErrorDescription(resp);
              const userFacingMessage = getNuveiUserFacingErrorMessage(resp, 'direct-pay');

              if (rawError && outcome !== 'approved') {
                logThirdPartyError(
                  new Error('Error in Nuvei createPayment method - Direct Payment'),
                  {
                    request,
                    response,
                    error: rawError,
                  }
                );
              }

              if (outcome === 'cancelled') {
                clearNuveiPaymentContext();
                showToast({ message: 'Payment was cancelled.', type: 'error' });
                return;
              }

              clearNuveiPaymentContext();

              switch (outcome) {
                case 'declined':
                case 'error':
                  setModal(null);
                  setModal('payment-status-direct-declined', {
                    message: userFacingMessage,
                    tenantBuildingId: tenantBuildingId,
                  });
                  break;
                case 'approved':
                  setModal(null);
                  setModal('payment-status-direct-successfull', {
                    tenantBuildingId: tenantBuildingId,
                    processDate: new Date().toISOString().split('T')[0],
                    tenantPaymentOptionId: selectedPaymentOption?.id,
                    earnedPoints,
                  });
                  break;
                default:
                  setModal(null);
                  setModal('payment-status-direct-declined', {
                    message: userFacingMessage,
                    tenantBuildingId: tenantBuildingId,
                  });
                  break;
              }
            }
          );
        } catch (error) {
          setIsPaying(false);
          clearNuveiPaymentContext();
          document.body.removeAttribute('data-nuvei-3ds-challenge');
          console.error(error);
          showToast({
            message: 'Error making payment: ' + error,
            type: 'error',
          });
          if (error instanceof Error) {
            logThirdPartyError(new Error('Error in Nuvei createPayment method - Direct Payment'), {
              request,
              response: null,
              error,
            });
          }
          return;
        }
      }
    };

    /* const createSubscriptionRequest = async () => {
      try {
        setIsPaying(true);
        await createSubscription(
          auth?.data.token,
          tenantBuildingId,
          selectedPaymentOption?.id || ''
        );
        setModal('successful-subscription');
      } catch (error) {
        setIsPaying(false);
        console.error(error);
        showToast({
          message: 'Subscription Not created: ' + error,
          type: 'error',
        });
        return;
      }
    }; */

    const createMITSubscriptionRequest = async () => {
      if (isPayingRef.current) return;
      try {
        setIsPaying(true);

        // 1. Save IP address when enabling autopay (before subscribeRent).
        // Non-blocking: if this fails, we still proceed with subscription.
        if (auth?.data.token) {
          try {
            const ipResponse = await axios.get(IPIFY_API_URL);
            const ipAddress = ipResponse?.data?.ip;
            if (ipAddress) {
              await saveUserIpAddress(auth.data.token, ipAddress);
            }
          } catch (error) {
            logThirdPartyError(
              new Error('Error fetching or saving user IP address before enabling autopay'),
              {
                api: 'ipify',
                endpoint: IPIFY_API_URL,
                error: error instanceof Error ? error.message : String(error),
              }
            );
          }
        }

        // 2. Subscribe rent
        if (!tenantBuildingId || !selectedPaymentOption?.id) return;
        await subscribeRent(auth?.data.token, tenantBuildingId, selectedPaymentOption.id);
        trackAutopayEnabled('autopay');

        setModal('payment-status-subscription-successfull', {
          tenantBuildingId: tenantBuildingId,
          processDate: new Date().toISOString().split('T')[0],
          tenantPaymentOptionId: selectedPaymentOption?.id,
          earnedPoints,
        });
      } catch (error) {
        console.error(error);
        logThirdPartyError(new Error('Autopay - subscription request failed'), {
          flow: 'autopay',
          step: 'subscribe',
          tenantBuildingId,
          paymentOptionId: selectedPaymentOption?.id,
          paymentMethod: selectedPaymentOption?.paymentMethod,
          paymentProcessor: selectedPaymentOption?.paymentProcessor,
          error,
        });
        setModal('payment-status-subscription-declined', {
          message: error instanceof Error ? error.message : String(error),
          tenantBuildingId: tenantBuildingId,
        });
      } finally {
        setIsPaying(false);
      }
    };

    React.useImperativeHandle(ref, () => ({
      createDirectPayment,
      createMITSubscriptionRequest,
      requestAutopay: () => setShowConfirmAutopay(true),
    }));
    const handleVerifyPAD = async () => {
      if (!selectedPaymentOption?.id || !auth?.data.token) {
        showToast({
          message: 'Missing payment option or authentication',
          type: 'error',
        });
        return;
      }

      const amount = parseFloat(verificationAmount);
      if (isNaN(amount) || amount < 0.01 || amount > 2.0) {
        setVerificationAmountError('Please enter a valid amount between $0.01 and $2.00');
        return;
      }

      // Clear any previous errors
      setVerificationAmountError('');

      try {
        setIsVerifying(true);
        await verifyPaymentOption(auth.data.token, selectedPaymentOption.id, amount);
        showToast({
          message: 'Verification initiated successfully',
          type: 'success',
        });
        // Refresh payment options to get updated status
        await refreshPaymentOptions();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'An error occurred while verifying payment option';
        logThirdPartyError(new Error('PAD - payment option verification failed'), {
          flow: 'pad_verification',
          step: 'verify_payment_option',
          paymentOptionId: selectedPaymentOption?.id,
          amount,
          error,
        });
        showToast({
          message: errorMessage,
          type: 'error',
        });
      } finally {
        setIsVerifying(false);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (paymentType === 'direct') {
        createDirectPayment();
      } else {
        setShowConfirmAutopay(true);
      }
    };

    const handleConfirmAutopay = () => {
      setShowConfirmAutopay(false);
      createMITSubscriptionRequest();
    };
    if (loading) {
      return <Spinner widthFull='!bg-transparent' />;
    }
    return (
      <>
        {/* Confirm autopay modal */}
        {showConfirmAutopay && (
          <div className='fixed inset-0 z-[100] flex items-center justify-center'>
            <div
              className='absolute inset-0 bg-[rgba(128,132,147,0.7)] backdrop-blur-sm'
              onClick={() => setShowConfirmAutopay(false)}
              aria-hidden
            />
            <motion.div
              className='relative w-[353px] max-w-[90vw] bg-white rounded-[12px] flex flex-col overflow-auto px-10 py-10 shadow-xl'
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <h3 className='text-main text-lg font-semibold text-center mb-2'>Enable autopay?</h3>
              <p className='text-[#475569] text-sm text-center mb-6'>
                Your rent will be paid automatically each month using your selected payment method.
              </p>
              <div className='flex justify-between gap-4'>
                <button
                  type='button'
                  className='cancelBtn flex-1'
                  onClick={() => setShowConfirmAutopay(false)}
                >
                  Cancel
                </button>
                <button type='button' className='submitBtn flex-1' onClick={handleConfirmAutopay}>
                  Enable autopay
                </button>
              </div>
            </motion.div>
          </div>
        )}

        <motion.section
          initial={{ opacity: 0, y: -5 }} // Starts slightly above
          animate={{ opacity: 1, y: 0 }} // Slides down to position
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className='flex flex-col xl:flex-row w-full'>
            {/* Left Column */}
            <div className='flex-[1]  flex flex-col gap-10 items-center justify-start'>
              <CardCarousal
                paymentOptions={paymentOptions}
                onActiveIndexChange={handleActiveIndexChange}
                editMode={false}
                paymentType={paymentType}
              ></CardCarousal>
              <div className='w-[300px] items-center justify-center flex mr-5 ml-5 xxs:ml-0 '>
                <button
                  className='submitBtn'
                  onClick={() =>
                    setModal('add-payment-method', {
                      tenantBuildingId: tenantBuildingId,
                      paymentType: paymentType,
                    })
                  }
                >
                  <div className='p-6 flex justify-between w-full'>
                    <span>Add payment method</span>
                    <Plus size={20} className='text-white' />
                  </div>
                </button>
              </div>
            </div>

            {/* Right Column */}
            <div className='flex-[4]  w-full'>
              <div className='bg-white w-full px-0 py-12 [@media(min-width:450px)]:p-12'>
                <div className='flex flex-col gap-10 items-center justify-start'>
                  <form
                    onSubmit={handleSubmit}
                    className='w-full  flex flex-col gap-10 ph-no-capture'
                    data-clarity-mask='true'
                  >
                    <div className='w-full flex flex-col gap-10'>
                      {isVerificationPending ? (
                        // Verification UI for any payment method (PAD or card) when status is VerificationPending
                        <>
                          {/* Verification Section - Primary Focus */}
                          <div className='flex flex-col gap-6 w-full  -mt-12'>
                            {/* Verification Header - Prominent Section */}
                            <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-[12px] p-6 shadow-sm'>
                              <div className='flex items-start gap-4'>
                                <div className='flex-shrink-0'>
                                  <div className='w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center'>
                                    <AlertCircle
                                      className='w-6 h-6 text-blue-600'
                                      strokeWidth={2.5}
                                    />
                                  </div>
                                </div>
                                <div className='flex-1'>
                                  <h3 className='text-lg font-semibold text-[#1E293B] mb-2'>
                                    Verify your payment method
                                  </h3>
                                  <p className='text-[14px] text-[#475569] leading-[1.6] mb-4'>
                                    To complete your payment method setup, we need to verify
                                    ownership. Enter the exact{' '}
                                    <span className='font-semibold text-[#334155]'>
                                      micro-deposit
                                    </span>{' '}
                                    amount that appears on your statement (between $0.01 and $2.00).
                                  </p>
                                  <div className='flex items-start gap-2 mb-4 rounded-[8px] bg-emerald-50 border border-emerald-200 px-3 py-2.5 w-full'>
                                    <RefreshCw className='w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5' />
                                    <p className='text-[13px] text-[#047857] leading-[1.5]'>
                                      The full micro-deposit will be automatically refunded within a
                                      few business days. No action is needed on your part.
                                    </p>
                                  </div>

                                  {/* Step-by-step instructions */}
                                  <div className='bg-white rounded-[8px] p-4 border border-blue-200'>
                                    <div className='flex flex-col gap-3'>
                                      <div className='flex items-start gap-3'>
                                        <div className='flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5'>
                                          1
                                        </div>
                                        <div className='flex-1'>
                                          <p className='text-[13px] font-medium text-[#334155] mb-1'>
                                            Check your statement
                                          </p>
                                          <p className='text-[12px] text-[#64748B]'>
                                            Look for the micro-deposit from us on your statement
                                            (between $0.01 and $2.00)
                                          </p>
                                        </div>
                                      </div>
                                      <div className='flex items-start gap-3'>
                                        <div className='flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5'>
                                          2
                                        </div>
                                        <div className='flex-1'>
                                          <p className='text-[13px] font-medium text-[#334155] mb-1'>
                                            Enter the micro-deposit amount
                                          </p>
                                          <p className='text-[12px] text-[#64748B]'>
                                            Enter the exact micro-deposit amount shown on your
                                            statement below
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Verification Input Section */}
                            <div className='bg-white border-2 border-blue-200 rounded-[12px] p-6 shadow-sm'>
                              <div className='flex flex-col gap-4'>
                                <div className='flex items-center gap-2 mb-2'>
                                  <CreditCard className='w-5 h-5 text-blue-600' />
                                  <label className='text-base font-semibold text-[#1E293B]'>
                                    Enter verification amount
                                  </label>
                                </div>
                                <p className='text-sm text-[#64748B] mb-4'>
                                  Enter the exact micro-deposit amount (e.g., 0.25) that appears on
                                  your statement
                                </p>

                                <div>
                                  <label className='block text-sm font-medium text-[#475569] mb-2'>
                                    Verification amount <span className='text-red-500'>*</span>
                                  </label>
                                  <div className='flex flex-col sm:flex-row gap-4 items-start'>
                                    <div className='flex-1'>
                                      <div className='relative'>
                                        <span className='absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B] text-lg font-medium'>
                                          $
                                        </span>
                                        <input
                                          type='text'
                                          value={verificationAmount}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            // Clear error when user starts typing
                                            setVerificationAmountError('');
                                            // Allow empty string
                                            if (val === '') {
                                              setVerificationAmount(val);
                                              return;
                                            }
                                            // Allow only numbers and one decimal point
                                            if (!/^\d*\.?\d*$/.test(val)) {
                                              return;
                                            }
                                            // Allow typing any valid numeric format while typing
                                            // Only restrict if the final parsed value exceeds 2.00
                                            const numVal = parseFloat(val);
                                            // Allow if it's just a decimal point, ends with decimal, or is a valid number <= 2.00
                                            // Explicitly allow "0" and values starting with "0."
                                            if (
                                              val === '.' ||
                                              val.endsWith('.') ||
                                              val === '0' ||
                                              val.startsWith('0.')
                                            ) {
                                              setVerificationAmount(val);
                                            } else if (!isNaN(numVal) && numVal <= 2.0) {
                                              setVerificationAmount(val);
                                            }
                                          }}
                                          placeholder='0.00'
                                          className='w-full pl-8 pr-4 py-3 border-2 border-[#E2E8F0] rounded-[8px] text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all'
                                          onKeyDown={(e) => {
                                            if (
                                              e.key === 'Enter' &&
                                              verificationAmount &&
                                              !isVerifying
                                            ) {
                                              handleVerifyPAD();
                                            }
                                          }}
                                        />
                                      </div>
                                      {verificationAmountError ? (
                                        <p className='text-xs text-red-500 mt-2'>
                                          {verificationAmountError}
                                        </p>
                                      ) : (
                                        <p className='text-xs text-[#94A3B8] mt-2'>
                                          Amount must be between $0.01 and $2.00
                                        </p>
                                      )}
                                    </div>
                                    <div className='sm:w-auto w-full'>
                                      <button
                                        type='button'
                                        onClick={handleVerifyPAD}
                                        disabled={
                                          isVerifying ||
                                          !verificationAmount ||
                                          (() => {
                                            const amount = parseFloat(verificationAmount);
                                            return isNaN(amount) || amount < 0.01 || amount > 2.0;
                                          })()
                                        }
                                        className='submitBtn px-8 py-3 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed h-fit w-full sm:w-auto flex items-center justify-center gap-2 min-w-[140px]'
                                      >
                                        {isVerifying ? (
                                          <>
                                            <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                                            <span>Verifying...</span>
                                          </>
                                        ) : (
                                          <>
                                            <span>Verify account</span>
                                            <ArrowRight className='w-4 h-4' />
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Help Text */}
                            <div className='bg-amber-50 border border-amber-200 rounded-[8px] p-4'>
                              <div className='flex items-start gap-3'>
                                <AlertCircle className='w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5' />
                                <div>
                                  <p className='text-sm text-[#92400E] font-medium mb-1'>
                                    Can't find the micro-deposit?
                                  </p>
                                  <p className='text-xs text-[#B45309]'>
                                    The micro-deposit typically appears within 1-3 business days.
                                    Check your recent transactions. If you still can't find it after
                                    3 days, please contact support.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Payment method details - Collapsed/Secondary Section (Bank for PAD, Card for card) */}
                          <div className='border-t border-gray-200 pt-6 mt-2'>
                            <details className='group'>
                              <summary className='cursor-pointer list-none flex items-center justify-between text-sm font-medium text-[#64748B] hover:text-[#475569] transition-colors'>
                                <span>
                                  {isPreAuthorizedDebit
                                    ? 'View bank account details'
                                    : 'View card details'}
                                </span>
                                <span className='text-xs'>▼</span>
                              </summary>
                              <div className='mt-4 flex flex-col gap-5'>
                                {isPreAuthorizedDebit ? (
                                  <>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Bank name*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={selectedPaymentOption?.bankName || ''}
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Account name*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={selectedPaymentOption?.bankAccountName || ''}
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                    <div className='flex flex-col sm:flex-row gap-5 w-full'>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Transit number*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            type='text'
                                            value={selectedPaymentOption?.bankTransitNumber || ''}
                                            disabled
                                            readOnly
                                            className='text-light/60'
                                          />
                                        </div>
                                      </div>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Institution number*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            type='text'
                                            value={
                                              selectedPaymentOption?.bankInstitutionNumber || ''
                                            }
                                            disabled
                                            readOnly
                                            className='text-light/60'
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Account number*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={
                                            selectedPaymentOption?.bankAccountNumber
                                              ? selectedPaymentOption.bankAccountNumber.length > 4
                                                ? `****${selectedPaymentOption.bankAccountNumber.slice(-4)}`
                                                : selectedPaymentOption.bankAccountNumber
                                              : ''
                                          }
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                    {selectedPaymentOption?.status && (
                                      <div className={'inputWrapper'}>
                                        <div className={'label-light'}>Status*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            type='text'
                                            value={getPaymentOptionStatusLabel(
                                              selectedPaymentOption.status
                                            )}
                                            disabled
                                            readOnly
                                            className='text-light/60'
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Card number*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={`**** **** **** ${selectedPaymentOption?.cardEndingDigits || ''}`}
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Cardholder name*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={selectedPaymentOption?.cardHolderName || ''}
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Expiration date*</div>
                                      <div className={'no-border-input-field'}>
                                        <input
                                          type='text'
                                          value={`${selectedPaymentOption?.expiryMonth || ''}/${selectedPaymentOption?.expiryYear || ''}`}
                                          disabled
                                          readOnly
                                          className='text-light/60'
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </details>
                          </div>
                        </>
                      ) : isPreAuthorizedDebit ? (
                        <>
                          {/* When verified, show bank details normally */}
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Bank name*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={selectedPaymentOption?.bankName || ''}
                                disabled
                                readOnly
                                className='text-light/60'
                              />
                            </div>
                          </div>
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Account name*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={selectedPaymentOption?.bankAccountName || ''}
                                disabled
                                readOnly
                                className='text-light/60'
                              />
                            </div>
                          </div>
                          <div className='flex flex-col sm:flex-row gap-5 w-full'>
                            <div className={'inputWrapper w-full'}>
                              <div className={'label-light'}>Transit number*</div>
                              <div className={'no-border-input-field'}>
                                <input
                                  type='text'
                                  value={selectedPaymentOption?.bankTransitNumber || ''}
                                  disabled
                                  readOnly
                                  className='text-light/60'
                                />
                              </div>
                            </div>
                            <div className={'inputWrapper w-full'}>
                              <div className={'label-light'}>Institution number*</div>
                              <div className={'no-border-input-field'}>
                                <input
                                  type='text'
                                  value={selectedPaymentOption?.bankInstitutionNumber || ''}
                                  disabled
                                  readOnly
                                  className='text-light/60'
                                />
                              </div>
                            </div>
                          </div>
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Account number*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={
                                  selectedPaymentOption?.bankAccountNumber
                                    ? selectedPaymentOption.bankAccountNumber.length > 4
                                      ? `****${selectedPaymentOption.bankAccountNumber.slice(-4)}`
                                      : selectedPaymentOption.bankAccountNumber
                                    : ''
                                }
                                disabled
                                readOnly
                                className='text-light/60'
                              />
                            </div>
                          </div>
                          {selectedPaymentOption?.status && (
                            <div className={'inputWrapper'}>
                              <div className={'label-light'}>Status*</div>
                              <div className={'no-border-input-field'}>
                                <input
                                  type='text'
                                  value={getPaymentOptionStatusLabel(selectedPaymentOption.status)}
                                  disabled
                                  readOnly
                                  className='text-light/60'
                                />
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        // Card details for other payment methods
                        <>
                          {selectedPaymentOption?.status &&
                            selectedPaymentOption.status !==
                              TenantPaymentOptionStatusEnum.Active && (
                              <div className='mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                                <p className='text-sm font-medium text-amber-800'>
                                  {getPaymentOptionStatusMessage(selectedPaymentOption.status)}
                                </p>
                                <p className='mt-1 text-xs text-amber-700'>
                                  Select another payment method or add a new card to pay.
                                </p>
                              </div>
                            )}
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Card number*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={`**** **** **** ${selectedPaymentOption?.cardEndingDigits || ''}`}
                                disabled
                                className='text-light/60'
                              />
                            </div>
                          </div>
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Cardholder name*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={selectedPaymentOption?.cardHolderName || ''}
                                disabled
                                className='text-light/60'
                              />
                            </div>
                          </div>
                          <div className='flex flex-col sm:flex-row gap-5 w-full'>
                            <div className={'inputWrapper w-full'}>
                              <div className={'label-light'}>Expiration date*</div>
                              <div className={'no-border-input-field'}>
                                <input
                                  type='text'
                                  value={`${selectedPaymentOption?.expiryMonth || ''}/${
                                    selectedPaymentOption?.expiryYear || ''
                                  }`}
                                  className='text-light/60'
                                  disabled
                                />
                              </div>
                            </div>

                            <div className='form-group w-full'>
                              <div className='item'>
                                <label className={'label-light'} htmlFor='card-cvc'>
                                  CVV*
                                </label>
                                <div
                                  id='card-cvc'
                                  className='input'
                                  style={{
                                    marginTop: '10px',
                                  }}
                                />
                              </div>
                              {cardErrors.ccCvc && (
                                <div className='text-red-500'>{cardErrors.ccCvc}</div>
                              )}
                            </div>
                          </div>
                          <div className={'inputWrapper'}>
                            <div className={'label-light'}>Billing address*</div>
                            <div className={'no-border-input-field'}>
                              <input
                                type='text'
                                value={selectedPaymentOption?.address || ''}
                                disabled
                                className='text-light/60'
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </motion.section>
      </>
    );
  }
);

export default CardSection;
